import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { echoService } from '@/services/echoService';
import { getErrorMessage } from '@/api/api';
import { useToast } from '@/context/ToastContext';
import type { AISuggestion } from '@/types/echo_creation';

interface AiWriterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPickSuggestion: (text: string) => void;
  formValues: { name: string; domain: string; category: string };
  isPublicFigure: boolean;
}

/** Modal that generates AI bio suggestions from user context or public figure data. */
export const AiWriterModal: React.FC<AiWriterModalProps> = ({
  isOpen,
  onClose,
  onPickSuggestion,
  formValues,
  isPublicFigure,
}) => {
  const { addToast } = useToast();

  const [knownFor, setKnownFor] = React.useState('');
  const [achievement, setAchievement] = React.useState('');
  const [description, setDescription] = React.useState('');

  const [isLoading, setIsLoading] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState<AISuggestion[]>([]);
  const [hasResults, setHasResults] = React.useState(false);

  const hasName = formValues.name.trim().length > 0;
  const canGenerate =
    hasName && (isPublicFigure || knownFor.trim().length > 0 || achievement.trim().length > 0);

  const handleGenerate = async () => {
    setIsLoading(true);
    setSuggestions([]);
    setHasResults(false);

    try {
      const additionalContext = isPublicFigure
        ? ''
        : [
            knownFor.trim() && `Known for: ${knownFor.trim()}`,
            achievement.trim() && `Biggest achievement: ${achievement.trim()}`,
            description.trim() && `Described by friends as: ${description.trim()}`,
          ]
            .filter(Boolean)
            .join('. ');

      const res = await echoService.aiSuggestBio({
        name: formValues.name,
        domain: formValues.domain,
        category: formValues.category,
        additional_context: additionalContext,
        is_public_figure: isPublicFigure,
      });

      setSuggestions(res.suggestions);
      setHasResults(true);
    } catch (error) {
      setSuggestions([]);
      addToast({
        title: 'AI Writer Failed',
        subtitle: getErrorMessage(error),
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePick = (text: string) => {
    onPickSuggestion(text);
    handleClose();
  };

  const handleClose = () => {
    setSuggestions([]);
    setHasResults(false);
    onClose();
  };

  const handleBack = () => {
    setSuggestions([]);
    setHasResults(false);
  };

  const inputClasses = clsx(
    'w-full rounded-10 px-4 py-3',
    'border border-gray-200 bg-white',
    'font-sans text-14 font-normal leading-160 tracking-neg-2 text-brand-800',
    'placeholder:text-gray-370',
    'focus:border-brand-300 focus:outline-none focus:ring-1 focus:ring-brand-300'
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <div className='fixed inset-0 z-modal flex items-center justify-center p-4'>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className='absolute inset-0 bg-black/40 backdrop-blur-sm'
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={clsx(
              'relative flex w-full max-w-screen-fold flex-col overflow-hidden',
              'max-h-[90vh]',
              'rounded-20 border-4 border-brand-800',
              'bg-white shadow-2xl'
            )}
            style={{
              background:
                'linear-gradient(180deg, #EEEAFF -102.61%, rgba(255, 255, 255, 0) 140.84%), #FFFFFF',
            }}
          >
            {/* Close */}
            <button
              onClick={handleClose}
              className='absolute right-4 top-4 z-10 rounded-full bg-red-50 p-2 text-red-400 transition-colors hover:bg-red-100'
            >
              <XMarkIcon className='size-5' />
            </button>

            <div className='custom-scrollbar overflow-y-auto p-8 pt-14'>
              {/* Header */}
              <h2 className='mb-2 font-sans text-24 font-bold leading-27 text-brand-800'>
                AI Writer
              </h2>
              <p className='mb-6 font-sans text-14 font-normal leading-24 text-gray-530'>
                {hasResults
                  ? 'Pick a bio that fits your style:'
                  : 'Help us write a great story for your echo.'}
              </p>

              {/* Results View */}
              {hasResults && suggestions.length > 0 && (
                <div className='flex flex-col gap-3'>
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      type='button'
                      onClick={() => handlePick(suggestion.text)}
                      className='rounded-10 border border-gray-200 bg-gray-50 p-4 text-left transition-colors hover:border-brand-300 hover:bg-brand-50'
                    >
                      {suggestion.style && (
                        <span className='mb-1 block font-sans text-12 font-semibold uppercase tracking-wide text-brand-600'>
                          {suggestion.style}
                        </span>
                      )}
                      <span className='font-sans text-14 leading-160 text-brand-800'>
                        {suggestion.text}
                      </span>
                    </button>
                  ))}

                  <button
                    type='button'
                    onClick={handleBack}
                    className='mt-2 font-sans text-14 font-semibold text-gray-650 underline decoration-solid transition-colors hover:text-brand-800'
                  >
                    Generate again
                  </button>
                </div>
              )}

              {/* Input View */}
              {!hasResults && (
                <div className='flex flex-col gap-6'>
                  {isPublicFigure ? (
                    <p className='font-sans text-14 font-normal leading-24 text-gray-530'>
                      We&apos;ll use our knowledge base to generate a bio based on the name, domain,
                      and category you&apos;ve already provided.
                    </p>
                  ) : (
                    <>
                      {/* Question 1 */}
                      <div className='flex flex-col gap-1.5'>
                        <label className='font-sans text-14 font-medium text-dark-100'>
                          What are you known for? <span className='text-red-400'>*</span>
                        </label>
                        <input
                          className={inputClasses}
                          placeholder='e.g., Personal training, life coaching, indie music...'
                          value={knownFor}
                          onChange={(e) => setKnownFor(e.target.value)}
                          maxLength={200}
                        />
                      </div>

                      {/* Question 2 */}
                      <div className='flex flex-col gap-1.5'>
                        <label className='font-sans text-14 font-medium text-dark-100'>
                          What&apos;s your biggest achievement?
                        </label>
                        <input
                          className={inputClasses}
                          placeholder='e.g., Built a 100K community, published a book...'
                          value={achievement}
                          onChange={(e) => setAchievement(e.target.value)}
                          maxLength={200}
                        />
                      </div>

                      {/* Question 3 */}
                      <div className='flex flex-col gap-1.5'>
                        <label className='font-sans text-14 font-medium text-dark-100'>
                          How would friends describe you?
                        </label>
                        <input
                          className={inputClasses}
                          placeholder='e.g., Energetic, straightforward, always motivating...'
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          maxLength={200}
                        />
                      </div>
                    </>
                  )}

                  {/* Generate Button */}
                  <div className='flex justify-center pt-2'>
                    <Button
                      variant='purple-dark'
                      onClick={handleGenerate}
                      disabled={!canGenerate}
                      isLoading={isLoading}
                      className='h-[44px] w-full text-16 font-bold uppercase tracking-wide md:w-[200px]'
                    >
                      GENERATE
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
